import { z } from "zod";

/// Wire-shape mirrors of the shore M16 attendance DTOs. Per webui T3/T4:
/// validate at the boundary with Zod, derive types via `z.infer`. Mirrors
/// `pulsewise-shore/src/modules/attendance/**/*.dto.ts`; drift breaks the
/// parser at runtime so it surfaces in dev, not silently in production.

/// Frappe's flat status enum, plus `not_marked`, which is ours.
///
/// `not_marked` means nobody has decided the day yet. It is NOT absent, and
/// the UI must never render it as one: it converts to absent only after the
/// workspace's grace window elapses, and showing it as an accusation before
/// then is exactly what the not-marked state exists to prevent.
export const ATTENDANCE_STATUSES = [
  "present",
  "absent",
  "on_leave",
  "half_day",
  "work_from_home",
  "not_marked",
] as const;
export const attendanceStatusSchema = z.enum(ATTENDANCE_STATUSES);
export type AttendanceStatus = z.infer<typeof attendanceStatusSchema>;

export const ATTENDANCE_HALF_DAY_STATUSES = ["present", "absent"] as const;
export const attendanceHalfDayStatusSchema = z.enum(ATTENDANCE_HALF_DAY_STATUSES);
export const attendanceHalfDayPeriodSchema = z.enum(["first_half", "second_half"]);
export type AttendanceHalfDayPeriod = z.infer<typeof attendanceHalfDayPeriodSchema>;

/// Who or what decided a day. `grace_conversion` means a job did, not a person.
export const ATTENDANCE_SOURCES = ["auto", "manual", "request", "grace_conversion"] as const;
export const attendanceSourceSchema = z.enum(ATTENDANCE_SOURCES);
export type AttendanceSource = z.infer<typeof attendanceSourceSchema>;

export const CHECKIN_LOG_TYPES = ["check_in", "check_out"] as const;
export const checkinLogTypeSchema = z.enum(CHECKIN_LOG_TYPES);
export type CheckinLogType = z.infer<typeof checkinLogTypeSchema>;

export const attendanceRecordSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    attendanceDate: z.string(),
    status: attendanceStatusSchema,
    halfDayStatus: attendanceHalfDayStatusSchema.nullable(),
    /// Nullable on new Shore responses, absent on older deployments. This
    /// identifies the worked half, not the absent half.
    workedHalfDayPeriod: attendanceHalfDayPeriodSchema.nullable().optional(),
    workingHours: z.number().nullable(),
    inTime: z.string().nullable(),
    outTime: z.string().nullable(),
    /// Wall-clock "HH:mm" in the WORKSPACE timezone, formatted by the server.
    /// Render these, not the instants: a shift is defined in the workspace's
    /// zone, so formatting client-side would show a 09:00 shift as 14:30 to a
    /// viewer sitting in another country.
    inTimeLocal: z.string().nullable(),
    outTimeLocal: z.string().nullable(),
    lateEntry: z.boolean(),
    earlyExit: z.boolean(),
    shiftId: z.string().nullable(),
    /// Travels with the record because listing shifts needs a permission a
    /// manager does not hold, and the day table needs it to explain an
    /// overnight pair of times.
    shiftName: z.string().nullable(),
    /// The shift's own window as wall-clock "HH:mm", so the table can tell a
    /// night shift from a morning one without parsing the free-text name.
    shiftStartTime: z.string().nullable(),
    shiftEndTime: z.string().nullable(),
    departmentId: z.string().nullable(),
    attendanceRequestId: z.string().nullable(),
    source: attendanceSourceSchema,
    notMarkedSince: z.string().nullable(),
    version: z.number(),
  })
  .strict();
export type AttendanceRecord = z.infer<typeof attendanceRecordSchema>;

export const attendanceRecordListSchema = z
  .object({ items: z.array(attendanceRecordSchema) })
  .strict();
export type AttendanceRecordList = z.infer<typeof attendanceRecordListSchema>;

/// Why a person has no attendance row, when that is the correct answer.
/// `null` means a record was expected and is genuinely missing.
export const unrecordedReasonSchema = z.enum(["no_shift", "holiday", "weekly_off"]);
export type UnrecordedReason = z.infer<typeof unrecordedReasonSchema>;

/**
 * Somebody in scope who has no attendance row for the day.
 *
 * The day board used to render only the rows that existed, so a workspace of
 * twenty-one whose engine had reached three showed three and said nothing
 * about the rest. `expected` keeps the fix from becoming its own problem: a
 * person with no shift, or a holiday, produces no record BY DESIGN, and
 * listing those beside somebody genuinely unaccounted for would trade silence
 * for eighteen false alarms.
 */
export const unrecordedMemberSchema = z
  .object({
    membershipId: z.string(),
    departmentId: z.string().nullable(),
    shiftId: z.string().nullable(),
    shiftName: z.string().nullable(),
    expected: z.boolean(),
    reason: unrecordedReasonSchema.nullable(),
  })
  .strict();
export type UnrecordedMember = z.infer<typeof unrecordedMemberSchema>;

export const attendanceDaySchema = z
  .object({
    items: z.array(attendanceRecordSchema),
    unrecorded: z.array(unrecordedMemberSchema),
  })
  .strict();
export type AttendanceDay = z.infer<typeof attendanceDaySchema>;

export const checkinSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    logType: checkinLogTypeSchema,
    time: z.string(),
    /// "HH:mm" in the workspace timezone; use when no shift-local value exists.
    timeLocal: z.string(),
    /// "HH:mm" in the shift zone when one is configured.
    timeShiftLocal: z.string().nullable().optional(),
    shiftId: z.string().nullable(),
    /// The day this log will be filed against. Null when off-shift, and for an
    /// overnight shift this is the day the shift STARTED, not the wall date.
    attendanceDate: z.string().nullable(),
    /// The log fell outside every shift window, so it will never be counted.
    offshift: z.boolean(),
    attendanceId: z.string().nullable(),
    skipAutoAttendance: z.boolean(),
    autoAttendanceSkipReason: z.string().nullable(),
    deviceId: z.string().nullable(),
  })
  .strict();
export type Checkin = z.infer<typeof checkinSchema>;

export const lastCheckinSchema = z
  .object({
    last: checkinSchema.nullable(),
    /// What the next tap records. Server-decided so the client never has to
    /// infer direction and cannot disagree with what actually gets written.
    nextLogType: checkinLogTypeSchema,
  })
  .strict();
export type LastCheckin = z.infer<typeof lastCheckinSchema>;

export const checkinListSchema = z.object({ items: z.array(checkinSchema) }).strict();

/// Who in the caller's scope is on the clock right now, from raw check-ins.
/// A person with no tap in the window is simply not listed.
export const presenceSchema = z
  .object({
    date: z.string(),
    items: z.array(
      z
        .object({
          membershipId: z.string(),
          state: z.enum(["in", "out"]),
          time: z.string(),
          timeLocal: z.string(),
        })
        .strict(),
    ),
  })
  .strict();
export type Presence = z.infer<typeof presenceSchema>;
export type PresenceEntry = Presence["items"][number];

export const ATTENDANCE_REQUEST_REASONS = ["work_from_home", "on_duty", "forgot_check_in"] as const;
export const attendanceRequestReasonSchema = z.enum(ATTENDANCE_REQUEST_REASONS);
export type AttendanceRequestReason = z.infer<typeof attendanceRequestReasonSchema>;

export const ATTENDANCE_REQUEST_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "cancelled",
] as const;
export const attendanceRequestStatusSchema = z.enum(ATTENDANCE_REQUEST_STATUSES);
export type AttendanceRequestStatus = z.infer<typeof attendanceRequestStatusSchema>;

export const attendanceRequestSchema = z
  .object({
    /// Discriminator for the shared approvals inbox, which merges this row
    /// with leave's `kind: "leave"` row and switches on `kind` to pick a
    /// detail card. A literal, not a bare string, so a future discriminated
    /// union over the two kinds is exhaustively checked at compile time.
    kind: z.literal("attendance"),
    id: z.string(),
    membershipId: z.string(),
    /// The requester's name, resolved server side from the workspace roster.
    /// Always a populated string (falls back to "Unknown member" server
    /// side), never null.
    displayName: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    halfDay: z.boolean(),
    halfDayDate: z.string().nullable(),
    halfDayPeriod: attendanceHalfDayPeriodSchema.nullable().optional(),
    reason: attendanceRequestReasonSchema,
    explanation: z.string().nullable(),
    includeHolidays: z.boolean(),
    status: attendanceRequestStatusSchema,
    decidedByUserId: z.string().nullable(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    version: z.number(),
    /// True when the row belongs to the caller. The server refuses a self
    /// decision, so a queue that renders Approve on one of these offers a
    /// button that can only fail.
    isOwn: z.boolean(),
    /// What is recorded TODAY for each day the request covers. Approving
    /// overwrites these, and the queue used to say so without ever saying what
    /// they were.
    currentDays: z.array(
      z.object({
        date: z.string(),
        status: attendanceStatusSchema.nullable(),
        halfDayStatus: attendanceHalfDayStatusSchema.nullable(),
      }),
    ),
    createdAt: z.string(),
    /// Same instant as `createdAt`, under the name leave's queue row uses as
    /// its merge key. Kept alongside `createdAt` (not in place of it) so the
    /// shared inbox can sort by one field name across both sources without
    /// this route renaming a field older clients already read.
    filedAt: z.string(),
  })
  .strict();
export type AttendanceRequest = z.infer<typeof attendanceRequestSchema>;

export const attendanceRequestListSchema = z
  .object({ items: z.array(attendanceRequestSchema) })
  .strict();

export const decideResultSchema = z
  .object({
    request: attendanceRequestSchema,
    daysApplied: z.number(),
    /// Dates the approval deliberately left alone. Surfaced so an approver who
    /// claimed five days and got three can see which two and why.
    skipped: z.array(z.object({ date: z.string(), reason: z.string() }).strict()),
  })
  .strict();
export type DecideResult = z.infer<typeof decideResultSchema>;

export interface CreateRequestInput {
  fromDate: string;
  toDate: string;
  reason: AttendanceRequestReason;
  explanation?: string;
  halfDay?: boolean;
  halfDayDate?: string;
  halfDayPeriod?: AttendanceHalfDayPeriod;
  includeHolidays?: boolean;
}

export const cancelledRequestSchema = z
  .object({ cancelled: z.literal(true), attendanceCancelled: z.number() })
  .strict();

/**
 * What the caller is scheduled for on one date.
 *
 * Not an attendance verdict. It answers "what am I meant to be doing", which
 * the page has to answer before anybody has checked in and therefore before
 * any record exists.
 */
export const myDaySchema = z
  .object({
    date: z.string(),
    shift: z
      .object({
        id: z.string(),
        name: z.string(),
        startTime: z.string(),
        endTime: z.string(),
      })
      .strict()
      .nullable(),
    /// The holiday's name when the date is not worked, else null. Covers
    /// generated week-offs, which carry a description like "Sunday week off".
    nonWorkingReason: z.string().nullable(),
    isHalfDayHoliday: z.boolean(),
    /// Whether the engine derives a day from taps made on a non-working day.
    /// Decides whether working a holiday is "recorded" or "kept, but it will
    /// not count towards a day".
    marksAttendanceOnHolidays: z.boolean(),
  })
  .strict();
export type MyDay = z.infer<typeof myDaySchema>;
